import { Check } from "lucide-react";
import { cn } from "@/components/ui/cn";

type VStepperStep = {
  label: string;
};

type VStepperProps = {
  steps: ReadonlyArray<VStepperStep>;
  currentStep: number;
  onStepClick?: (index: number) => void;
  className?: string;
};

export function VStepper({
  steps,
  currentStep,
  onStepClick,
  className,
}: VStepperProps): React.JSX.Element {
  return (
    <ol className={cn("flex flex-wrap items-center gap-2", className)}>
      {steps.map((step, idx) => {
        const status = idx < currentStep ? "done" : idx === currentStep ? "current" : "todo";
        const interactive = onStepClick && idx < currentStep;
        return (
          <li key={step.label} className="flex items-center gap-2">
            <button
              type="button"
              onClick={interactive ? () => onStepClick?.(idx) : undefined}
              disabled={!interactive}
              className={cn(
                "flex items-center gap-2 rounded-md px-2 py-1 text-sm transition-colors",
                interactive ? "cursor-pointer hover:bg-muted" : "cursor-default",
              )}
            >
              <span
                className={cn(
                  "flex size-6 items-center justify-center rounded-full text-xs font-semibold",
                  status === "done" && "bg-success-50 text-success-700 ring-1 ring-inset ring-success-600/25",
                  status === "current" && "bg-primary-600 text-on-primary ring-2 ring-primary-200",
                  status === "todo" && "bg-muted text-muted-foreground",
                )}
              >
                {status === "done" ? <Check className="size-3.5" strokeWidth={3} /> : idx + 1}
              </span>
              <span
                className={cn(
                  "hidden font-medium md:inline",
                  status === "current" ? "text-foreground" : "text-muted-foreground",
                )}
              >
                {step.label}
              </span>
            </button>
            {idx < steps.length - 1 ? (
              <span className="hidden h-px w-6 bg-border md:inline-block" aria-hidden />
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

export type { VStepperProps, VStepperStep };
